import React from 'react';
import { useParams, useHistory } from 'react-router-dom';
import axios from 'axios';
import swal from 'sweetalert2';
import styled from 'styled-components';
import { AuthContext } from 'contexts';
import errorReporter from '@app/errorReporter';
import { ParsedNeed } from '@bb/domain/dist/types/dto/commonTypes';
import { Spin, theme, message } from 'components';

export type PartialNeedType = Pick<
  ParsedNeed,
  // @ts-expect-error: createdAt is not in the ParsedNeed type
  'type' | 'subject' | 'note' | 'createdAt' | 'thumbnail' | 'canEdit'
> & {
  id?: string;
  rep: Partial<ParsedNeed['rep']>;
};

export type FileType = {
  order?: number;
  key?: string;
  id: string;
  name: string;
  url: string;
  filetype: 'image/jpg' | 'image/png' | 'application/pdf';
};

type NeedContextType = {
  emptyNeed: PartialNeedType;
  need: PartialNeedType;
  setNeed: React.Dispatch<React.SetStateAction<PartialNeedType>>;
  files: FileType[];
  setFiles: React.Dispatch<React.SetStateAction<FileType[]>>;
  saveFiles: (newFiles: FileType[]) => void;
  reorderFiles: (reorderedFiles: FileType[]) => void;
  deleteFile: (fileToDelete: FileType) => void;
  thumbnailLoading: boolean;
  deleteNeed: () => void;
  saveNeed: (newNeed: PartialNeedType) => void;
  isAuthorizedToEdit: boolean;
};

const emptyNeed: PartialNeedType = {
  type: undefined,
  subject: undefined,
  note: '',
  createdAt: undefined,
  canEdit: true,
  rep: {
    id: undefined,
    email: '',
    firstName: '',
    lastName: '',
  },
};

const NeedContext = React.createContext<NeedContextType>(null);

const NeedContextProvider: React.FC = ({ children }) => {
  const { needId } = useParams<Record<string, string>>();
  const { auth } = React.useContext(AuthContext);
  const [need, setNeed] = React.useState<PartialNeedType>(emptyNeed);
  const [files, setFiles] = React.useState<NeedContextType['files']>([]);
  const [loading, setLoading] = React.useState(true);
  const [thumbnailLoading, setThumbnailLoading] = React.useState(false);
  const history = useHistory();

  const formatFiles = (filesFromRequest: NeedContextType['files']) =>
    filesFromRequest
      .map((file, i) => ({
        ...file,
        key: file.id,
        order: file.order || i,
      }))
      .sort((a, b) => {
        if (a.order > b.order) return 1;
        if (a.order < b.order) return -1;
        return 0;
      });

  const fetchNeed = async () => {
    if (!needId) {
      setNeed(emptyNeed);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get<PartialNeedType>(`/need/${needId}`);
      setNeed(data);
      setLoading(false);
    } catch (err) {
      errorReporter.error(err);
      message.error('Server error. Please try again later.');
      history.push('/needs/0');
    }
  };

  const fetchFiles = async () => {
    if (!needId) return;
    try {
      const { data } = await axios.get<NeedContextType['files']>(
        `/need/${needId}/files`,
      );
      setFiles(formatFiles(data));
    } catch (err) {
      errorReporter.error(err);
      message.error('Server error. Please try again later.');
      history.push('/needs/0');
    }
  };

  const saveFiles = async (newFiles: NeedContextType['files']) => {
    if (!needId) return;
    const formattedFiles = newFiles.map((file, i) => ({
      ...file,
      key: file.id,
      order: files.length + i,
    }));
    const previousFiles = files;
    setFiles(prev => [...prev, ...formattedFiles]);
    setThumbnailLoading(true);
    try {
      const { data } = await axios.post<{
        thumbnail: string;
        files: NeedContextType['files'];
      }>(`/need/${needId}/files`, {
        files: formattedFiles,
      });
      if (typeof data.thumbnail === 'string') {
        setNeed(prev => ({ ...prev, thumbnail: data.thumbnail }));
      }
      setFiles([...previousFiles, ...formatFiles(data.files)]);
      setThumbnailLoading(false);
    } catch (err) {
      errorReporter.error(err);
      message.error('Server error. Please try again later.');
      history.push('/needs/0');
    }
  };

  const reorderFiles = async (reorderedFiles: NeedContextType['files']) => {
    setFiles(reorderedFiles);
    setThumbnailLoading(true);

    try {
      const { data } = await axios.post<{ thumbnail: string }>(
        `/need/${needId}/files/reorder`,
        { files: reorderedFiles },
      );
      setNeed(prev => ({ ...prev, thumbnail: data.thumbnail }));
      setThumbnailLoading(false);
    } catch (err) {
      errorReporter.error(err);
      message.error('Server error. Please try again later.');
      history.push('/needs/0');
    }
  };

  const deleteFile = async (fileToDelete: FileType) => {
    setFiles(prev => prev.filter(file => file.id !== fileToDelete.id));
    setThumbnailLoading(true);

    try {
      const { data } = await axios.delete<{ thumbnail: string }>(
        `/need/${needId}/files/${fileToDelete.id}`,
      );
      if (typeof data.thumbnail === 'string') {
        setNeed(prev => ({ ...prev, thumbnail: data.thumbnail }));
      }
      setThumbnailLoading(false);
    } catch (err) {
      errorReporter.error(err);
      message.error('Server error. Please try again later.');
      history.push('/needs/0');
    }
  };

  const deleteNeed = () => {
    swal
      .fire({
        title: `Delete Need`,
        text: `Are you sure you want to delete this need?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonColor: theme.getColor('danger'),
        confirmButtonText: 'Delete',
        closeButtonHtml: 'Cancel',
        reverseButtons: true,
      })
      .then(async ({ value }) => {
        if (value) {
          try {
            await axios.delete(`/need/${needId}`);
            message.success('Need deleted successfully.');
            history.push('/needs/0');
          } catch (err) {
            errorReporter.error(err);
            message.error('Server error. Please try again later.');
          }
        }
      });
  };

  const saveNeed = async (newNeed: PartialNeedType) => {
    setLoading(true);
    try {
      const { data } = await axios.post<PartialNeedType>(
        `/need/${needId || ''}`,
        {
          ...newNeed,
          rep: auth.isAdmin ? newNeed.rep.id : auth.id,
        },
      );
      message.success('Need updated successfully.');
      setNeed(data);
      history.replace(`/needs/${data.id}/view`);
    } catch (err) {
      errorReporter.error(err);
      message.error('Server error. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    fetchNeed();
    fetchFiles();
  }, [needId]);

  const isAuthorizedToEdit = need?.canEdit;

  if (!need || loading) {
    return (
      <StyledSpinWrapper>
        <Spin size="large" center />
      </StyledSpinWrapper>
    );
  }

  return (
    <NeedContext.Provider
      value={{
        emptyNeed,
        need,
        setNeed,
        files,
        setFiles,
        saveFiles,
        reorderFiles,
        deleteFile,
        thumbnailLoading,
        deleteNeed,
        saveNeed,
        isAuthorizedToEdit,
      }}
    >
      {children}
    </NeedContext.Provider>
  );
};

const StyledSpinWrapper = styled.div`
  position: relative;
  height: calc(50vh + 200px);
`;

export { NeedContextProvider };
export default NeedContext;
